import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, User as UserIcon, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ADMIN_EMAIL = 'admin@bytesizepm.com';

const links = [
  { label: 'Programs', to: '/programs' },
  { label: 'Community', to: '/community' },
  { label: 'Resources', to: '/blog' },
  { label: 'About Rajni', to: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMenu(false); }, [pathname]);

  const doLogout = async () => { setMenu(false); await logout(); navigate('/'); };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#F4F4F7]/85 backdrop-blur-md border-b border-black/5' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
        <Link to="/" className="font-display text-[20px] tracking-tight text-[#0E1726]">
          <span className="font-bold">bytesize</span>
          <span className="text-[#2E6BFF] font-bold">PM</span>
          <span className="text-[#5B6472] font-medium text-[16px]"> by Rajni</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-[15px] font-medium transition-colors hover:text-[#2E6BFF] ${
                pathname === l.to ? 'text-[#2E6BFF]' : 'text-[#0E1726]'
              }`}
            >
              {l.label}
            </Link>
          ))}
          {user ? (
            <div className="relative">
              <button onClick={() => setMenu(!menu)} className="flex items-center gap-2">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="w-9 h-9 rounded-full object-cover border border-black/10" />
                ) : (
                  <span className="w-9 h-9 rounded-full bg-[#2E6BFF]/12 text-[#2E6BFF] font-bold flex items-center justify-center">{user.name?.charAt(0)}</span>
                )}
              </button>
              {menu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-[0_20px_50px_-20px_rgba(14,23,38,0.35)] border border-black/5 py-2">
                  <div className="px-4 py-2 border-b border-black/5">
                    <div className="font-semibold text-[#0E1726] text-sm truncate">{user.name}</div>
                    <div className="text-xs text-[#5B6472] truncate">{user.email}</div>
                  </div>
                  <Link to="/profile" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#0E1726] hover:bg-black/5"><UserIcon size={15} /> My profile</Link>
                  {user.email === ADMIN_EMAIL && (
                    <Link to="/admin" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#2E6BFF] font-medium hover:bg-black/5"><LayoutDashboard size={15} /> Admin dashboard</Link>
                  )}
                  <button onClick={doLogout} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#0E1726] hover:bg-black/5"><LogOut size={15} /> Log out</button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={login} data-cta="Sign in" className="bg-[#0E1B2E] text-white text-[15px] font-semibold px-5 py-2.5 rounded-full hover:bg-[#1b2a44] transition-colors">
              Sign in
            </button>
          )}
        </div>

        <button className="md:hidden text-[#0E1726]" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-[#F4F4F7] border-b border-black/5 px-5 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="py-2.5 text-[16px] font-medium text-[#0E1726]">
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link to="/profile" className="py-2.5 text-[16px] font-medium text-[#0E1726]">My profile</Link>
              {user.email === ADMIN_EMAIL && (
                <Link to="/admin" className="py-2.5 text-[16px] font-medium text-[#2E6BFF]">Admin dashboard</Link>
              )}
              <button onClick={doLogout} className="mt-2 border border-black/15 text-[#0E1726] text-center text-[15px] font-semibold px-5 py-3 rounded-full">Log out</button>
            </>
          ) : (
            <button onClick={login} data-cta="Sign in" className="mt-2 bg-[#0E1B2E] text-white text-center text-[15px] font-semibold px-5 py-3 rounded-full">Sign in</button>
          )}
        </div>
      )}
    </header>
  );
}
